import { userService } from '@/services/user.service';
import { exportService } from '@/services/export.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Activity,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Download,
  Edit2,
  Plus, Search,
  Trash2,
  Users,
  X
} from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import DepartmentForm, { DepartmentFormData } from '@/components/forms/DepartmentForm';
import Pagination from '@/components/ui/Pagination';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { GROUPS_PERMISSION } from '@/constants/constant-permissions';

// This is the raw group object from the API with enhanced statistics
interface ApiDepartment {
  _id: string;
  parent_group_key: string;
  group_key: string;
  group_name: string;
  group_description?: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
  // Enhanced fields from actual API response
  groupId: string;
  groupName: string;
  groupKey: string;
  totalMembers: number;
  assignedCourses: number;
  avgProgress: number;
  completionRate: number;
  engagementLevel: string;
  memberStats: {
    completed: number;
    inProgress: number;
    notStarted: number;
    overdue: number;
  };
  topPerformers: Array<{
    userId: string;
    userName: string;
    score: number;
  }>;
  certification_count: number;
  certification_stats: {
    completed: number;
    in_progress: number;
    not_started: number;
  };
}

// This is the transformed group object for the UI
interface Department {
  id: string;
  groupKey: string;
  name: string;
  description: string;
  totalMembers: number;
  assignedCourses: number;
  avgProgress: number;
  completionRate: number;
  engagementLevel: string;
  certificationCount: number;
  memberStats: {
    completed: number;
    inProgress: number;
    notStarted: number;
    overdue: number;
  };
  certificationStats: {
    completed: number;
    inProgress: number;
    notStarted: number;
  };
  lastActivity: string;
}

export default function Departments() {
  usePermissionRouteAccess([
    GROUPS_PERMISSION.ADMIN_GROUPS_READ,
    GROUPS_PERMISSION.ADMIN_GROUPS_WRITE,
  ]);
  const canWrite = useHasPermission([GROUPS_PERMISSION.ADMIN_GROUPS_WRITE]);

  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<{ id: string; name: string } | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const debouncedSearch = useDebounce(searchQuery, DEFAULT_DEBOUNCE_DELAY);
  const [exportLoading, setExportLoading] = useState<'csv' | 'excel' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['groups', debouncedSearch, page, pageSize],
    queryFn: () => userService.getGroups({
      group_name: debouncedSearch,
      skip: (page - 1) * pageSize,
      limit: pageSize,
    }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: DepartmentFormData) => userService.createGroup(data),
    onSuccess: () => {
      toast.success('Department created successfully');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setShowAddModal(false);
      setIsSubmitting(false);
      setPage(1);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.error_description || 'Failed to create department');
      setIsSubmitting(false);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: Partial<DepartmentFormData> }) =>
      userService.updateGroup(key, data),
    onSuccess: () => {
      toast.success('Department updated successfully');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setShowEditModal(false);
      setSelectedDepartment(null);
      setIsSubmitting(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.error_description  || 'Failed to update department');
      setIsSubmitting(false);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (key: string) => userService.deleteGroup(key),
    onSuccess: () => {
      toast.success('Department deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setShowDeleteModal(false);
      setSelectedDepartment(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.error_description || 'Failed to delete department');
      setIsSubmitting(false);
    }
  });

  const departments: Department[] = useMemo(() => {
    const apiDepartments: ApiDepartment[] = data?.data || [];
    return apiDepartments.map((d: ApiDepartment) => ({
      id: d._id,
      groupKey: d.groupKey || d.group_key,
      name: d.groupName || d.group_name,
      description: d.group_description || 'No description available',
      totalMembers: d.totalMembers || 0,
      assignedCourses: d.assignedCourses || 0,
      avgProgress: Math.round(d.avgProgress || 0),
      completionRate: Math.round(d.completionRate || 0),
      engagementLevel: d.engagementLevel || 'low',
      certificationCount: d.certification_count || 0,
      memberStats: d.memberStats || {
        completed: 0,
        inProgress: 0,
        notStarted: 0,
        overdue: 0
      },
      certificationStats: {
        completed: d.certification_stats?.completed || 0,
        inProgress: d.certification_stats?.in_progress || 0,
        notStarted: d.certification_stats?.not_started || 0
      },
      lastActivity: format(new Date(d.updatedAt), 'PPpp'),
    }));
  }, [data]);

  // Calculate header statistics
  const headerStats = useMemo(() => {
    return {
      totalDepartments: data?.meta_data?.metrics_data?.total_groups || 'N/A',
      departmentInUse: data?.meta_data?.metrics_data?.groups_with_users || 'N/A',
      activeEmployeesInGroups: data?.meta_data?.metrics_data?.total_users_with_groups || 'N/A',
      avgCompletion: data?.meta_data?.metrics_data?.overall_avg_score || 'N/A',
      totalCourseAssignments: data?.meta_data?.metrics_data?.total_group_course_assignments || 0,
      totalCertificationAssignments: data?.meta_data?.metrics_data?.total_group_certification_assignments || 0,
      totalLearningPathAssignments: data?.meta_data?.metrics_data?.total_group_learning_path_assignments || 0,
    };
  }, [data, departments]);

  const totalCount = data?.meta_data?.metrics_data?.total_groups || 0;

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(format);

      // Call the export service with format and search query (no skip/limit)
      const result = await exportService.exportGroupsList(format, debouncedSearch);

      if (result.success) {
        toast.success(`${result.count} departments exported to ${format.toUpperCase()} successfully`);
      } else {
        toast.error(result.message || 'No data to export');
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || 'Failed to export departments');
    } finally {
      setExportLoading(null);
    }
  };

  const handleCreate = async (data: DepartmentFormData) => {
    setIsSubmitting(true);
    await createMutation.mutateAsync(data);
  };

  const handleUpdate = async (data: DepartmentFormData) => {
    if (!selectedDepartment) return;
    setIsSubmitting(true);
    await updateMutation.mutateAsync({
      key: selectedDepartment.groupKey,
      data: {
        group_name: data.group_name,
        group_description: data.group_description
      }
    });
  };

  const handleDelete = async () => {
    if (!selectedDepartment) return;
    setIsSubmitting(true);
    await deleteMutation.mutateAsync(selectedDepartment.groupKey);
    setIsSubmitting(false);
  };

  const openEditModal = (dept: Department) => {
    setSelectedDepartment(dept);
    setShowEditModal(true);
  };

  const openDeleteModal = (dept: Department) => {
    setDepartmentToDelete({ id: dept.id, name: dept.name });
    setSelectedDepartment(dept);
    setShowDeleteModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold mb-2">Department Management</h1>
            <p className="text-brand-purple-100 text-sm sm:text-base">
              Organize teams, assign courses, and track departmental learning progress
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => canWrite && setShowAddModal(true)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center justify-center gap-2 px-4 py-2 bg-white text-brand-purple-600 rounded-lg transition-colors whitespace-nowrap ${
                canWrite ? 'hover:bg-brand-purple-50' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Add Department</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 animate-pulse">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="h-3 bg-white/20 rounded w-24 mb-2"></div>
                    <div className="h-6 sm:h-8 bg-white/20 rounded w-16"></div>
                  </div>
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded flex-shrink-0"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-brand-purple-100 text-xs sm:text-sm truncate">Total Departments</p>
                  <p className="text-xl sm:text-2xl font-bold">{headerStats.totalDepartments}</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-brand-purple-100 text-xs sm:text-sm truncate">Department In Use</p>
                  <p className="text-xl sm:text-2xl font-bold">{headerStats.departmentInUse}</p>
                </div>
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-brand-purple-100 text-xs sm:text-sm truncate">Users with Departments</p>
                  <p className="text-xl sm:text-2xl font-bold">{headerStats.activeEmployeesInGroups}</p>
                </div>
                <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-brand-purple-100 text-xs sm:text-sm truncate mb-3">Total Assignments</p>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg sm:text-xl font-bold text-white">{headerStats.totalCourseAssignments}</p>
                      <p className="text-xs text-brand-purple-200 mt-0.5">Courses</p>
                    </div>
                    <div className="h-8 w-px bg-brand-purple-300/30"></div>
                    <div className="text-center">
                      <p className="text-lg sm:text-xl font-bold text-white">{headerStats.totalCertificationAssignments}</p>
                      <p className="text-xs text-brand-purple-200 mt-0.5">Certs</p>
                    </div>
                    <div className="h-8 w-px bg-brand-purple-300/30"></div>
                    <div className="text-center">
                      <p className="text-lg sm:text-xl font-bold text-white">{headerStats.totalLearningPathAssignments}</p>
                      <p className="text-xs text-brand-purple-200 mt-0.5">Paths</p>
                    </div>
                  </div>
                </div>
                <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200 flex-shrink-0" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters and Actions */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => canWrite && handleExport('csv')}
              disabled={!canWrite || exportLoading !== null}
              title={!canWrite ? 'You do not have permission' : undefined}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading === 'csv' ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {exportLoading === 'csv' ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={() => canWrite && handleExport('excel')}
              disabled={!canWrite || exportLoading !== null}
              title={!canWrite ? 'You do not have permission' : undefined}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading === 'excel' ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {exportLoading === 'excel' ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Departments Grid */}
      {isLoading ? (
        <div className="text-center py-10">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {departments.map((department) => (
              <div key={department.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-brand-purple-500 to-brand-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{department.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">{department.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-500 truncate">
                          <Activity className="w-3 h-3 inline mr-1" />
                          {department.lastActivity}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
                  <div className="text-center p-2 sm:p-3 bg-blue-50 rounded-lg">
                    <p className="text-lg sm:text-2xl font-bold text-blue-600">{department.totalMembers}</p>
                    <p className="text-xs text-gray-600 mt-1">Employees</p>
                  </div>
                  <div className="text-center p-2 sm:p-3 bg-purple-50 rounded-lg">
                    <p className="text-lg sm:text-2xl font-bold text-purple-600">{department.assignedCourses}</p>
                    <p className="text-xs text-gray-600 mt-1">Courses</p>
                  </div>
                  <div className="text-center p-2 sm:p-3 bg-green-50 rounded-lg">
                    <p className="text-lg sm:text-2xl font-bold text-green-600">{department.certificationCount}</p>
                    <p className="text-xs text-gray-600 mt-1">Certifications</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t">
                  <div className="flex gap-2 justify-center sm:justify-start">
                    <button
                      onClick={() => canWrite && navigate(
                        `/admin/groups/${department.groupKey}/courses`,
                        { state: { from: '/admin/users/departments' } }
                      )}
                      disabled={!canWrite}
                      className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!canWrite ? 'You do not have permission' : 'View Courses'}
                    >
                      <BookOpen className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => canWrite && openEditModal(department)}
                      disabled={!canWrite}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!canWrite ? 'You do not have permission' : 'Edit Department'}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => canWrite && openDeleteModal(department)}
                      disabled={!canWrite}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!canWrite ? 'You do not have permission' : 'Delete Department'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => canWrite && navigate(
                      `/admin/groups/${department.groupKey}/courses`,
                      { state: { from: '/admin/users/departments' } }
                    )}
                    disabled={!canWrite}
                    className={`flex items-center justify-center gap-1 text-sm font-medium transition-colors ${
                      canWrite ? 'text-primary-600 hover:text-primary-700' : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={!canWrite ? 'You do not have permission' : undefined}
                  >
                    <span>View Details</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <Pagination
            currentPage={page}
            totalPages={Math.ceil(totalCount / pageSize)}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={(newPage) => {
              setPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onItemsPerPageChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />
        </>
      )}

      {/* Add Department Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Department</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <DepartmentForm
              onSubmit={handleCreate}
              isSubmitting={isSubmitting}
              onCancel={() => setShowAddModal(false)}
            />
          </div>
        </div>
      )}

      {/* Assign Courses Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Assign Courses</h3>
            <p className="text-gray-600 mb-4">Course assignment is not available in this demo.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditModal && selectedDepartment && (
        <div className="fixed inset-0  bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Department</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedDepartment(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <DepartmentForm
              initialData={{
                group_key: selectedDepartment.groupKey,
                group_name: selectedDepartment.name,
                group_description: selectedDepartment.description,
                parent_group_key: 'root'
              }}
              onSubmit={handleUpdate}
              isSubmitting={isSubmitting}
              isEditing
              onCancel={() => {
                setShowEditModal(false);
                setSelectedDepartment(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Delete Department Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedDepartment(null);
          setDepartmentToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Department"
        description={selectedDepartment && selectedDepartment.totalMembers > 0
          ? `Are you sure you want to delete this department? This department has ${selectedDepartment.totalMembers} users assigned.`
          : "Are you sure you want to delete this department?"
        }
        itemName={departmentToDelete?.name}
        isLoading={isSubmitting}
      />
    </div>
  );
}